import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import API from "./config/API";

function FloorRoomBedSelector() {
  const navigate = useNavigate();
  const location = useLocation();

  const user = JSON.parse(localStorage.getItem("user"));
  const emailId = user?.emailId || "";

  const [floorDropdownOpen, setFloorDropdownOpen] = useState(false);

  // Floor name received from previous page
  const floorName = location.state?.floorName || "";

  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState("");
  const [selectedFloorData, setSelectedFloorData] = useState(null);
  const [popup, setPopup] = useState("");

  // =====================================================
  // POPUP
  // =====================================================

  const showPopup = (msg) => {
    setPopup(msg);

    setTimeout(() => {
      setPopup("");
    }, 2000);
  };

  // =====================================================
  // FETCH ROOMS
  // =====================================================

  const fetchRooms = async (selectedFloorName) => {
    if (!selectedFloorName) {
      setRooms([]);
      return;
    }

    try {
      const loggedUser = JSON.parse(localStorage.getItem("user"));

      const response = await fetch(
        `${API.V1}/floor_rooms/getRoomsByFloor?emailId=${encodeURIComponent(
          emailId,
        )}&floorName=${encodeURIComponent(selectedFloorName)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${loggedUser?.jwtToken}`,
          },
        },
      );

      if (response.status === 401) {
        alert("Session Expired. Please login again.");
        localStorage.clear();
        navigate("/");
        return;
      }

      const data = await response.json();

      if (data.statusCode === 200) {
        setRooms(data.data || []);
      } else {
        setRooms([]);
        showPopup(data.message || "Failed to load rooms");
      }
    } catch (error) {
      console.error(error);
      showPopup("Failed to load rooms");
      setRooms([]);
    }
  };

  // =====================================================
  // FETCH FLOORS
  // =====================================================

  useEffect(() => {
    if (!emailId) return;

    const fetchFloors = async () => {
      try {
        const loggedUser = JSON.parse(localStorage.getItem("user"));

        const response = await fetch(
          `${API.V1}/floor_rooms/getFloors?emailId=${encodeURIComponent(
            emailId,
          )}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${loggedUser?.jwtToken}`,
            },
          },
        );

        if (response.status === 401) {
          alert("Session Expired. Please login again.");
          localStorage.clear();
          navigate("/");
          return;
        }

        const data = await response.json();

        if (data.statusCode === 200) {
          const floorList = data.data?.existedfloors || [];

          setFloors(floorList);

          // =================================================
          // AUTO SELECT FLOOR
          // =================================================

          if (floorName) {
            const selected = floorList.find((f) => f.floorName === floorName);

            if (selected) {
              setSelectedFloor(selected.floorId);
              setSelectedFloorData(selected);

              fetchRooms(selected.floorName);
            }
          }
        } else {
          showPopup(data.message || "Failed to load floors");
        }
      } catch (error) {
        console.error(error);
        showPopup("Failed to load floors");
      }
    };

    fetchFloors();
  }, [emailId, floorName, navigate]);

  // =====================================================
  // FLOOR CHANGE
  // =====================================================

  const handleFloorChange = (e) => {
    const floorId = Number(e.target.value);

    setSelectedFloor(floorId);

    const floor = floors.find((f) => f.floorId === floorId);

    if (!floor) {
      setSelectedFloorData(null);
      setRooms([]);
      return;
    }

    // Store complete selected floor object
    setSelectedFloorData(floor);

    // Fetch rooms
    fetchRooms(floor.floorName);
  };

  // =====================================================
  // FLOOR BED CREATION STATUS
  // =====================================================

  const availableBeds = Number(
    selectedFloorData?.availableBedByFloorRooms || 0,
  );

  const isBedCreationExceeded = availableBeds <= 0;

  // =====================================================
  // CREATE BED
  // =====================================================

  const handleCreateBed = (room) => {
    const createdBeds = Number(room.createdBeds || 0);
    const maxBeds = Number(room.maxBedsCount || 0);

    if (createdBeds >= maxBeds) {
      showPopup("Bed Creation Exceed for this Room");
      return;
    }

    // Also check floor-level availability
    if (availableBeds <= 0) {
      showPopup("Bed Creation Exceed for this Floor");
      return;
    }

    navigate("/createBedForm", {
      state: {
        floorId: room.floorId,
        floorName: room.floorName,
        roomId: room.roomId,
        roomName: room.roomName,
        createdBeds: room.createdBeds,
        maxBedsCount: room.maxBedsCount,
      },
    });
  };

  return (
    <div style={styles.container}>
      {/* =====================================================
          POPUP
      ===================================================== */}

      {popup && <div style={styles.popup}>{popup}</div>}

      <div style={styles.card}>
        {/* =====================================================
            TITLE
        ===================================================== */}

        <h2 style={styles.title}>🛏 Bed Management</h2>

        {/* =====================================================
            FLOOR DROPDOWN
        ===================================================== */}

        <div style={styles.dropdownContainer}>
          {/* DROPDOWN HEADER */}

          <div
            style={styles.dropdownHeader}
            onClick={() => setFloorDropdownOpen(!floorDropdownOpen)}
          >
            {selectedFloorData ? (
              <div style={styles.selectedFloorContent}>
                <span>{selectedFloorData.floorName}</span>

                {Number(selectedFloorData.availableBedByFloorRooms || 0) > 0 ? (
                  <span style={styles.greenBadge}>🟢 Available</span>
                ) : (
                  <span style={styles.redBadge}>🔴 Exceed</span>
                )}
              </div>
            ) : (
              <span style={styles.placeholder}>Select Floor</span>
            )}

            <span style={styles.arrow}>{floorDropdownOpen ? "▲" : "▼"}</span>
          </div>

          {/* DROPDOWN OPTIONS */}

          {floorDropdownOpen && (
            <div style={styles.dropdownMenu}>
              {floors.map((floor) => {
                const availableBeds = Number(
                  floor.availableBedByFloorRooms || 0,
                );

                const isAvailable = availableBeds > 0;

                return (
                  <div
                    key={floor.floorId}
                    style={{
                      ...styles.dropdownOption,
                      background:
                        selectedFloor === floor.floorId ? "#eff6ff" : "#fff",
                    }}
                    onClick={() => {
                      setSelectedFloor(floor.floorId);

                      setSelectedFloorData(floor);

                      setFloorDropdownOpen(false);

                      fetchRooms(floor.floorName);
                    }}
                  >
                    {/* FLOOR NAME */}

                    <span style={styles.optionFloorName}>
                      {floor.floorName}
                    </span>

                    {/* BED COUNT */}

                    <span style={styles.optionBedCount}>
                      {availableBeds} beds
                    </span>

                    {/* STATUS */}

                    {isAvailable ? (
                      <span style={styles.greenBadge}>🟢 Available</span>
                    ) : (
                      <span style={styles.redBadge}>🔴 Exceed</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* =====================================================
            SELECTED FLOOR BED STATUS
        ===================================================== */}

        {selectedFloorData && (
          <div
            style={{
              ...styles.floorStatus,
              background: isBedCreationExceeded ? "#fee2e2" : "#dcfce7",
              border: isBedCreationExceeded
                ? "1px solid #ef4444"
                : "1px solid #22c55e",
            }}
          >
            <div style={styles.floorStatusTitle}>
              {selectedFloorData.floorName}
            </div>

            <div style={styles.floorStatusInfo}>
              Total Rooms:{" "}
              <strong>{selectedFloorData.totalRoomsCountByFloor || 0}</strong>
            </div>

            <div style={styles.floorStatusInfo}>
              Available Beds: <strong>{availableBeds}</strong>
            </div>

            {isBedCreationExceeded ? (
              <div style={styles.floorExceeded}>❌ Bed Creation Exceed</div>
            ) : (
              <div style={styles.floorAvailable}>✅ Bed Creation Available</div>
            )}
          </div>
        )}

        {/* =====================================================
            ROOM LIST
        ===================================================== */}

        {rooms.length === 0 ? (
          <div style={styles.noRooms}>
            No Rooms Available For Selected Floor
          </div>
        ) : (
          <div style={styles.grid}>
            {rooms.map((room) => {
              const createdBeds = Number(room.createdBeds || 0);

              const maxBeds = Number(room.maxBedsCount || 0);

              const roomAvailableBeds = Math.max(maxBeds - createdBeds, 0);

              const roomLimitReached = createdBeds >= maxBeds;

              // Floor-level limit OR room-level limit
              const isLimitReached = isBedCreationExceeded || roomLimitReached;

              return (
                <div
                  key={room.roomId}
                  style={{
                    ...styles.cardBox,
                    border: isLimitReached
                      ? "2px solid #ef4444"
                      : "2px solid #22c55e",
                  }}
                >
                  {/* ROOM NAME */}

                  <div style={styles.roomName}>{room.roomName}</div>

                  {/* BED COUNT */}

                  <div
                    style={{
                      ...styles.count,
                      color: isLimitReached ? "#dc2626" : "#2563eb",
                    }}
                  >
                    {createdBeds} / {maxBeds}
                  </div>

                  {/* ROOM AVAILABLE */}

                  <div style={styles.availableText}>
                    Available: <strong>{roomAvailableBeds}</strong>
                  </div>

                  {/* WARNING */}

                  {roomLimitReached && (
                    <div style={styles.warning}>⚠ Bed Limit Reached</div>
                  )}

                  {/* FLOOR LIMIT WARNING */}

                  {!roomLimitReached && isBedCreationExceeded && (
                    <div style={styles.warning}>
                      ⚠ Floor Bed Creation Exceed
                    </div>
                  )}

                  {/* CREATE BED BUTTON */}

                  <button
                    disabled={isLimitReached}
                    style={{
                      ...styles.link,
                      background: isLimitReached ? "#9ca3af" : "#2563eb",
                      cursor: isLimitReached ? "not-allowed" : "pointer",
                    }}
                    onClick={() => handleCreateBed(room)}
                  >
                    ➕ Create Bed
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default FloorRoomBedSelector;

// =====================================================
// STYLES
// =====================================================

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg,#dbeafe,#f8fafc,#eef2ff)",
    padding: "30px",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    fontFamily: "Segoe UI, sans-serif",
  },

  card: {
    width: "100%",
    maxWidth: "950px",
    background: "#fff",
    borderRadius: "20px",
    padding: "30px",
    boxShadow: "0 15px 35px rgba(0,0,0,0.12)",
  },

  title: {
    textAlign: "center",
    fontSize: "30px",
    fontWeight: "700",
    color: "#1e3a8a",
    marginBottom: "30px",
  },

  select: {
    width: "100%",
    padding: "14px",
    borderRadius: "10px",
    border: "1px solid #d1d5db",
    outline: "none",
    fontSize: "16px",
    marginBottom: "20px",
    background: "#fff",
    cursor: "pointer",
  },

  // =====================================================
  // FLOOR STATUS
  // =====================================================

  floorStatus: {
    borderRadius: "14px",
    padding: "18px 20px",
    marginBottom: "25px",
    textAlign: "center",
  },

  floorStatusTitle: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "10px",
  },

  floorStatusInfo: {
    fontSize: "15px",
    color: "#374151",
    marginTop: "5px",
  },

  floorAvailable: {
    marginTop: "10px",
    color: "#15803d",
    fontSize: "16px",
    fontWeight: "700",
  },

  floorExceeded: {
    marginTop: "10px",
    color: "#dc2626",
    fontSize: "16px",
    fontWeight: "700",
  },

  // =====================================================
  // ROOM GRID
  // =====================================================

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
    gap: "20px",
  },

  cardBox: {
    background: "#ffffff",
    borderRadius: "16px",
    padding: "20px",
    textAlign: "center",
    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
    transition: "0.3s",
  },

  roomName: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "12px",
  },

  count: {
    fontSize: "17px",
    fontWeight: "700",
    marginBottom: "8px",
  },

  availableText: {
    fontSize: "14px",
    color: "#374151",
    marginBottom: "10px",
  },

  warning: {
    color: "#dc2626",
    fontWeight: "700",
    marginBottom: "12px",
    fontSize: "14px",
  },

  link: {
    width: "100%",
    padding: "12px",
    border: "none",
    borderRadius: "10px",
    color: "#fff",
    fontWeight: "700",
    fontSize: "15px",
    cursor: "pointer",
    transition: "0.3s",
  },

  noRooms: {
    textAlign: "center",
    fontSize: "18px",
    color: "#6b7280",
    padding: "40px",
    background: "#f8fafc",
    borderRadius: "12px",
  },

  popup: {
    position: "fixed",
    top: "20px",
    right: "20px",
    background: "#111827",
    color: "#fff",
    padding: "14px 22px",
    borderRadius: "10px",
    fontWeight: "600",
    zIndex: 999,
    boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
  },
  dropdownContainer: {
    position: "relative",
    width: "100%",
    marginBottom: "25px",
  },

  dropdownHeader: {
    width: "100%",
    minHeight: "50px",
    padding: "12px 15px",
    borderRadius: "10px",
    border: "1px solid #d1d5db",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    boxSizing: "border-box",
  },

  selectedFloorContent: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#1e293b",
  },

  placeholder: {
    color: "#6b7280",
    fontSize: "16px",
  },

  arrow: {
    fontSize: "12px",
    color: "#64748b",
  },

  dropdownMenu: {
    position: "absolute",
    top: "58px",
    left: "0",
    right: "0",
    background: "#fff",
    border: "1px solid #d1d5db",
    borderRadius: "10px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
    zIndex: 1000,
    overflow: "hidden",
  },

  dropdownOption: {
    minHeight: "55px",
    padding: "10px 15px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    borderBottom: "1px solid #f1f5f9",
    cursor: "pointer",
    boxSizing: "border-box",
  },

  optionFloorName: {
    flex: "1",
    fontSize: "16px",
    fontWeight: "600",
    color: "#1e293b",
  },

  optionBedCount: {
    fontSize: "13px",
    color: "#64748b",
  },

  greenBadge: {
    background: "#dcfce7",
    color: "#15803d",
    padding: "5px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "700",
    whiteSpace: "nowrap",
  },

  redBadge: {
    background: "#fee2e2",
    color: "#dc2626",
    padding: "5px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "700",
    whiteSpace: "nowrap",
  },
};
